import Phaser from "phaser";

import "./styles/main.css";

import {
  markBootLoadingFailed,
  mountBootLoadingShell,
  pushBootLoadingLog,
  setBootLoadingProgress,
  syncBootLoadingShellFrame,
} from "./bootstrap/bootLoading";
import { INTERNAL_VIEWPORT, calculateViewportLayout } from "./bootstrap/viewport";
import { configureRuntimeSession, getRuntimeSession, type RuntimeSessionOptions } from "./core/RuntimeSession";
import { MemorySaveAdapter } from "./model/SaveManager";
import { BootScene } from "./scenes/BootScene";
import { EndingScene } from "./scenes/EndingScene";
import { HomeScene } from "./scenes/HomeScene";
import { LevelScene } from "./scenes/LevelScene";
import { LevelSelectScene } from "./scenes/LevelSelectScene";
import { NoteScene } from "./scenes/NoteScene";

type WindowWithSignalTestConfig = Window & typeof globalThis & {
  __SIGNAL_TEST_CONFIG__?: RuntimeSessionOptions;
  __SIGNAL_GAME__?: Phaser.Game;
};

const SCALE_STORAGE_KEY = "signal-viewport-scale";
const AUTO_SCALE_MARKER = 0;

type ScaleMode = "auto" | "manual";

interface ScaleState {
  mode: ScaleMode;
  value: number;
}

function readSavedScale(): ScaleState | null {
  const raw = localStorage.getItem(SCALE_STORAGE_KEY);
  if (raw === null) return null;
  const n = Number(raw);
  if (n === AUTO_SCALE_MARKER) {
    return { mode: "auto", value: computeAutoScale() };
  }
  if (Number.isInteger(n) && n >= 1) {
    return { mode: "manual", value: n };
  }
  return null;
}

function writeScale(state: ScaleState): void {
  const stored = state.mode === "auto" ? AUTO_SCALE_MARKER : state.value;
  localStorage.setItem(SCALE_STORAGE_KEY, String(stored));
}


function resolveInitialScale(): ScaleState {
  return readSavedScale() ?? { mode: "auto", value: computeAutoScale() };
}

function syncBootShellLayout(scale: number): void {
  const header = document.querySelector<HTMLElement>(".nb-header");
  const scaleBar = document.querySelector<HTMLElement>(".nb-scale-bar");
  const headerHeight = header?.offsetHeight ?? 0;
  const scaleBarHeight = scaleBar?.offsetHeight ?? 0;
  const availableWidth = window.innerWidth;
  const availableHeight = window.innerHeight - headerHeight - scaleBarHeight;
  const fittedLayout = calculateViewportLayout(availableWidth, availableHeight);
  const frameWidth = INTERNAL_VIEWPORT.width * scale + 6;
  const frameHeight = INTERNAL_VIEWPORT.height * scale + 6;
  const topOffset = headerHeight + scaleBarHeight + Math.max(0, fittedLayout.offsetY - 3);

  syncBootLoadingShellFrame({
    width: frameWidth,
    height: frameHeight,
    topOffset,
  });
}

function applyCanvasScale(scale: number): void {
  const canvas = document.querySelector<HTMLCanvasElement>("#game-root canvas");
  if (canvas === null) return;
  canvas.style.width = `${INTERNAL_VIEWPORT.width * scale}px`;
  canvas.style.height = `${INTERNAL_VIEWPORT.height * scale}px`;
  canvas.style.imageRendering = "pixelated";
  syncBootShellLayout(scale);
}

function updateScaleDisplay(state: ScaleState): void {
  const valueEl = document.querySelector<HTMLSpanElement>("#scale-value");
  if (valueEl !== null) {
    valueEl.textContent = state.mode === "auto" ? `${state.value}x (auto)` : `${state.value}x`;
  }
}

const appRoot = document.querySelector<HTMLDivElement>("#app");

if (appRoot === null) {
  throw new Error("App root element '#app' was not found.");
}

let scaleState = resolveInitialScale();

appRoot.innerHTML = `
  <header class="nb-header">
    <img class="nb-header-icon" src="/favicon.svg" alt="Unpatterned icon" />
    <div class="nb-header-text">
      <span class="nb-header-title">Unpatterned</span>
      <span class="nb-header-subtitle">LDJAM 59 · THEME "SIGNAL"</span>
    </div>
  </header>
  <div class="nb-scale-bar">
    <button id="scale-down" class="nb-btn" tabindex="-1" aria-label="Decrease scale">−</button>
    <span id="scale-value">${scaleState.value}x${scaleState.mode === "auto" ? " (auto)" : ""}</span>
    <button id="scale-up" class="nb-btn" tabindex="-1" aria-label="Increase scale">+</button>
    <button id="scale-auto" class="nb-btn ${scaleState.mode === "auto" ? "nb-btn-active" : ""}" tabindex="-1" aria-label="Toggle auto scale">Auto Scale</button>
    <button id="clear-save" class="nb-btn" tabindex="-1" aria-label="Clear save data">Delete Savedata</button>
  </div>
  <div class="nb-game-wrapper">
    <div id="game-root" class="nb-game-frame"></div>
  </div>
  <footer class="nb-control-footer" aria-label="Keyboard controls">
    WASD move · Z undo · R restart · Space jump · Tab notes · Enter confirm · Esc cancel
  </footer>
  <div id="debug-root" aria-label="Debug information" style="display:none"></div>
`;

const gameRoot = document.querySelector<HTMLDivElement>("#game-root");

if (gameRoot === null) {
  throw new Error("Game root element '#game-root' was not found.");
}

if (scaleState.mode === "auto") {
  scaleState = { mode: "auto", value: computeAutoScale() };
  updateScaleDisplay(scaleState);
}

mountBootLoadingShell();
syncBootShellLayout(scaleState.value);
pushBootLoadingLog("BOOT", "main bundle ready");
setBootLoadingProgress(18, "main bundle ready");

function computeAutoScale(): number {
  const header = document.querySelector<HTMLElement>(".nb-header");
  const scaleBar = document.querySelector<HTMLElement>(".nb-scale-bar");

  const headerHeight = header?.offsetHeight ?? 0;
  const scaleBarHeight = scaleBar?.offsetHeight ?? 0;

  const availableWidth = window.innerWidth;
  const availableHeight = window.innerHeight - headerHeight - scaleBarHeight;

  const layout = calculateViewportLayout(availableWidth, availableHeight);
  return layout.scale;
}

function updateButtonStates(): void {
  const downBtn = document.querySelector<HTMLButtonElement>("#scale-down");
  const upBtn = document.querySelector<HTMLButtonElement>("#scale-up");
  const autoBtn = document.querySelector<HTMLButtonElement>("#scale-auto");

  if (downBtn !== null) {
    downBtn.disabled = scaleState.mode === "auto" || scaleState.value <= 1;
  }
  if (upBtn !== null) {
    upBtn.disabled = scaleState.mode === "auto";
  }
  if (autoBtn !== null) {
    autoBtn.classList.toggle("nb-btn-active", scaleState.mode === "auto");
  }
}

function handleScaleChange(newScale: number): void {
  scaleState = { mode: "manual", value: newScale };
  updateScaleDisplay(scaleState);
  syncBootShellLayout(scaleState.value);
  applyCanvasScale(scaleState.value);
  updateButtonStates();
  writeScale(scaleState);
}

function handleAutoScale(): void {
  if (scaleState.mode === "auto") {
    scaleState = { mode: "manual", value: computeAutoScale() };
  } else {
    scaleState = { mode: "auto", value: computeAutoScale() };
  }
  updateScaleDisplay(scaleState);
  syncBootShellLayout(scaleState.value);
  applyCanvasScale(scaleState.value);
  updateButtonStates();
  writeScale(scaleState);
}

function refreshAutoScale(): void {
  if (scaleState.mode === "auto") {
    scaleState = { mode: "auto", value: computeAutoScale() };
    updateScaleDisplay(scaleState);
    syncBootShellLayout(scaleState.value);
    applyCanvasScale(scaleState.value);
  }
}

document.querySelector<HTMLButtonElement>("#scale-down")?.addEventListener("click", () => {
  if (scaleState.mode === "auto") return;
  if (scaleState.value > 1) {
    handleScaleChange(scaleState.value - 1);
  }
});

document.querySelector<HTMLButtonElement>("#scale-up")?.addEventListener("click", () => {
  if (scaleState.mode === "auto") return;
  handleScaleChange(scaleState.value + 1);
});

document.querySelector<HTMLButtonElement>("#scale-auto")?.addEventListener("click", () => {
  handleAutoScale();
});

// ── Clear Save: confirm pattern ──────────────────────────────────────
{
  const CLEAR_CONFIRM_TIMEOUT_MS = 3000;
  const clearBtn = document.querySelector<HTMLButtonElement>("#clear-save");
  let confirmTimer = 0;

  function resetClearButton(): void {
    if (clearBtn === null) return;
    clearBtn.textContent = "Delete Savedata";
    clearBtn.classList.remove("nb-btn-danger");
    clearBtn.setAttribute("aria-label", "Clear save data");
    confirmTimer = 0;
  }

  clearBtn?.addEventListener("click", async () => {
    if (confirmTimer === 0) {
      // First click: enter confirm state
      clearBtn.textContent = "Confirm Delete?";
      clearBtn.classList.add("nb-btn-danger");
      clearBtn.setAttribute("aria-label", "Delete save data");
      confirmTimer = window.setTimeout(() => {
        resetClearButton();
      }, CLEAR_CONFIRM_TIMEOUT_MS);
      return;
    }

    // Second click within timeout: actually clear
    window.clearTimeout(confirmTimer);
    resetClearButton();

    const session = getRuntimeSession();
    await session.clearSave();
    window.location.reload();
  });
}

window.addEventListener("resize", () => {
  refreshAutoScale();
});

async function bootstrap(): Promise<void> {
  let runtimeConfigured = false;

  try {
    pushBootLoadingLog("INIT", "configuring runtime session");
    setBootLoadingProgress(24, "configuring runtime session");
    const testConfig = import.meta.env.DEV
      ? (window as WindowWithSignalTestConfig).__SIGNAL_TEST_CONFIG__
      : undefined;
    const options: RuntimeSessionOptions | undefined = testConfig
      ? { ...testConfig, saveAdapter: testConfig.saveAdapter ?? new MemorySaveAdapter() }
      : undefined;
    await configureRuntimeSession(options);
    runtimeConfigured = true;
    pushBootLoadingLog("OK", "runtime session ready", "success");
    setBootLoadingProgress(36, "runtime session ready");
  } catch (error) {
    markBootLoadingFailed(
      "Failed to initialize runtime session",
      error instanceof Error ? error.message : String(error),
    );
    const debugRoot = document.querySelector<HTMLElement>("#debug-root");
    if (debugRoot !== null) {
      debugRoot.style.display = "block";
      debugRoot.textContent = error instanceof Error ? error.message : String(error);
    }
  }

  if (!runtimeConfigured) {
    return;
  }

  pushBootLoadingLog("BOOT", "creating phaser game");
  setBootLoadingProgress(42, "creating phaser game");
  const gameConfig: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: gameRoot,
    width: INTERNAL_VIEWPORT.width,
    height: INTERNAL_VIEWPORT.height,
    backgroundColor: "#000000",
    pixelArt: true,
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: [BootScene, HomeScene, LevelScene, LevelSelectScene, NoteScene, EndingScene],
  };

  const game = new Phaser.Game(gameConfig);

  if (import.meta.env.DEV) {
    (window as WindowWithSignalTestConfig).__SIGNAL_GAME__ = game;
  }

  game.events.once("ready", () => {
    applyCanvasScale(scaleState.value);
    updateButtonStates();
  });
}

void bootstrap();
